import { Component, Input, forwardRef, OnInit, OnChanges, SimpleChanges, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Router } from '@angular/router';
import { Observable, of } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { ApprovalService } from './approval/services/approval.service';
import { ApprovalFlowDefinition } from './approval/models/approval-flow-definition.model';
import { ResolvedApprovalStep } from './approval/models/approval-flow.model';
import { ApprovalFlowPreviewComponent } from './approval-flow-preview/approval-flow-preview.component';
import { environment } from '../../services/global-environment';
import { AuthService, RoleOption } from '../../services/auth.service';
import { DepartmentService } from './approval/services/department.service';
import { UserService } from './approval/services/user.service';
import { Department } from './approval/models/approval-flow-definition.model';
import { I18nService } from '../../services/i18n.service';
import { TranslatePipe } from '../../pipes/translate.pipe';

@Component({
  selector: 'app-form-approval-flow',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslatePipe],
  template: `
    <div class="form-section approval-flow-section">
      <label [attr.for]="id" [class.required-field]="required">
        <i class="fas fa-route"></i> {{ label }}
      </label>

      <!-- 預覽模式：顯示解析後的簽核流程資訊 -->
      <div *ngIf="preview" class="approval-flow-display">
        <!-- 根據 approvalFlowId 或 value 顯示真實的簽核流程 -->
        <div *ngIf="selectedFlow" class="flow-preview-card">
          <div class="flow-preview-header">
            <i class="fas fa-check-circle flow-icon"></i>
            <span class="flow-name">{{ selectedFlow.name }}</span>
            <span class="flow-status active">{{ 'form.approval_flow.enabled' | translate }}</span>
          </div>

          <div class="flow-preview-content">
            <div class="flow-description" *ngIf="selectedFlow.description">
              {{ selectedFlow.description }}
            </div>

            <!-- 詳細的簽核步驟顯示 -->
            <div class="flow-steps" *ngIf="selectedFlow.steps?.length">
              <div class="steps-label" (click)="toggleStepsExpanded()" [class.expanded]="isStepsExpanded">
                <span>{{ 'form.approval_flow.approval_steps' | translate }}:</span>
                <i class="fas fa-chevron-down toggle-icon" [class.rotated]="isStepsExpanded"></i>
              </div>

              <div class="steps-content" [class.expanded]="isStepsExpanded" *ngIf="isStepsExpanded">
                <div *ngFor="let step of selectedFlow.steps; let i = index" class="step-item">
                  <div class="step-header">
                    <div class="step-number">{{ i + 1 }}</div>
                    <div class="step-name">{{ step.name }}</div>
                    <div class="step-optional" *ngIf="step.optional">{{ 'form.approval_flow.optional' | translate }}</div>
                  </div>

                  <div class="step-details">
                    <div class="detail-row">
                      <span class="label">{{ 'form.approval_flow.approver' | translate }}：</span>
                      <span class="value">
                        {{ step.approverType === 'role_based' ? ('form.approval_flow.role_based' | translate) : ('form.approval_flow.specific_user' | translate) }}
                      </span>
                    </div>

                    <div class="detail-row" *ngIf="step.approverType === 'role_based'">
                      <span class="label">{{ 'form.approval_flow.department_role' | translate }}：</span>
                      <span class="value">
                        <span [class.dynamic-department]="step.isDynamicDepartment">
                          {{ getDepartmentName(step.departmentId || '', step) }}
                        </span>
                        - {{ getRoleTranslatedName(step.roleId || '') }}
                      </span>
                    </div>

                    <div class="detail-row" *ngIf="step.approverType === 'specific_user'">
                      <span class="label">{{ 'form.approval_flow.designated_personnel' | translate }}：</span>
                      <span class="value">{{ getUserName(step.userId || '') }}</span>
                    </div>

                    <!-- 當 getuserlist 為 true 時，顯示可簽核人員列表 -->
                    <div class="detail-row" *ngIf="getuserlist && hasUserList(step)">
                      <span class="label">{{ 'form.approval_flow.eligible_approvers' | translate }}：</span>
                      <div class="value">
                        <div class="user-list">
                          <span *ngFor="let user of getUserList(step); let last = last" class="user-item">
                            {{ user.name }}
                            <span *ngIf="!last" class="separator">、</span>
                          </span>
                        </div>
                      </div>
                    </div>

                    <div class="detail-row">
                      <span class="label">{{ 'form.approval_flow.require_comment' | translate }}：</span>
                      <span class="value">{{ step.requireComment ? ('common.yes' | translate) : ('common.no' | translate) }}</span>
                    </div>

                    <div class="detail-row">
                      <span class="label">{{ 'form.approval_flow.require_signature' | translate }}：</span>
                      <span class="value">{{ step.requireSignature ? ('common.yes' | translate) : ('common.no' | translate) }}</span>
                    </div>

                    <!-- 自訂表單欄位顯示 -->
                    <div class="detail-row" *ngIf="step.customFormItems && step.customFormItems.length > 0">
                      <span class="label">{{ 'form.approval_flow.custom_form_fields' | translate }}：</span>
                      <div class="value">
                        <div class="custom-form-items">
                          <div *ngFor="let item of step.customFormItems; let i = index" class="custom-form-item">
                            <span class="item-index">{{ i + 1 }}.</span>
                            <span class="item-label">{{ item.label }}</span>
                            <span class="item-type">({{ getFormItemTypeDisplay(item.type) }})</span>
                            <span class="required-indicator" *ngIf="item.required">*</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 沒有選擇流程時的顯示 -->
        <div *ngIf="!selectedFlow" class="no-flow-selected">
          <i class="fas fa-info-circle"></i>
          <span>{{ 'form.approval_flow.no_flow_configured' | translate }}</span>
        </div>
      </div>

      <!-- 編輯模式：下拉選單（在屬性設定中使用） -->
      <div *ngIf="!preview">
        <select
          [id]="id"
          [name]="name"
          [ngModel]="value"
          (ngModelChange)="onFlowChange($event)"
          (blur)="onTouched()"
          class="form-input approval-flow-select"
        >
          <option value="" disabled>{{ placeholder }}</option>
          <option *ngFor="let flow of availableFlows" [value]="flow.id">
            {{ flow.name }}
          </option>
        </select>

        <!-- 載入狀態 -->
        <div *ngIf="loading" class="loading-indicator">
          <i class="fas fa-spinner fa-spin"></i>
          <span>{{ 'form.approval_flow.loading' | translate }}</span>
        </div>

        <!-- 錯誤狀態 -->
        <div *ngIf="error" class="error-message">
          <i class="fas fa-exclamation-triangle"></i>
          <span>{{ error }}</span>
        </div>

        <!-- 選中流程的詳細資訊（編輯模式） -->
        <div *ngIf="selectedFlow" class="flow-details">
          <div class="flow-info-header">
            <i class="fas fa-info-circle"></i>
            <span>{{ 'form.approval_flow.flow_details' | translate }}</span>
          </div>

          <div class="flow-info-content">
            <div class="flow-name">{{ selectedFlow.name }}</div>
            <div class="flow-description" *ngIf="selectedFlow.description">
              {{ selectedFlow.description }}
            </div>

            <div class="flow-meta">
              <span class="meta-item">
                <i class="fas fa-list"></i>
                {{ (selectedFlow.steps.length || 0) + ' ' + ('form.approval_flow.approval_steps_count' | translate) }}
              </span>
              <span class="meta-item" *ngIf="selectedFlow.formTypes?.length">
                <i class="fas fa-tags"></i>
                {{ 'form.approval_flow.applicable' | translate }}: {{ selectedFlow.formTypes.join(', ') }}
              </span>
            </div>

            <!-- 簽核步驟預覽 -->
            <div class="steps-preview" *ngIf="selectedFlow.steps?.length">
              <div class="steps-header">{{ 'form.approval_flow.approval_steps' | translate }}:</div>
              <div class="steps-list">
                <div
                  *ngFor="let step of selectedFlow.steps; let i = index"
                  class="step-item"
                >
                  <span class="step-number">{{ step.order }}</span>
                  <span class="step-name">{{ step.name }}</span>
                  <span class="step-approver">
                    <ng-container *ngIf="step.approverType === 'role_based'">
                      ({{ step.roleName || ('form.approval_flow.role' | translate) }})
                    </ng-container>
                    <ng-container *ngIf="step.approverType === 'specific_user'">
                      ({{ step.userName || ('form.approval_flow.specific_user' | translate) }})
                    </ng-container>
                  </span>
                </div>
         </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FormApprovalFlowComponent),
      multi: true
    }
  ],
  styleUrls: ['total-component.scss']
})
export class FormApprovalFlowComponent implements ControlValueAccessor, OnInit, OnChanges {
  @Input() label: string = '';
  @Input() id: string = '';
  @Input() name: string = '';
  @Input() placeholder: string = '';
  @Input() required: boolean = false;
  @Input() preview: boolean = false; // 新增：是否為預覽模式
  @Input() approvalFlowId?: string; // 新增：指定的簽核流程 ID (可選)
  @Input() getuserlist: boolean = false; // 新增：是否在簽核元件中顯示人員列表
  @Input() initiatorInfo: any; // 新增：填寫者資訊


  value: string = '';
  availableFlows: ApprovalFlowDefinition[] = [];
  selectedFlow: ApprovalFlowDefinition | null = null;
  loading = false;
  error: string | null = null;

  // 新增：資料來源
  departments: Department[] = [];

  // 新增：收合控制
  isStepsExpanded: boolean = false;

  // ControlValueAccessor callback 函數
  onChange = (value: any) => {};
  onTouched = () => {};

  constructor(
    private approvalService: ApprovalService,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private departmentService: DepartmentService,
    private userService: UserService,
    private http: HttpClient,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {
    // 設定預設值（如果沒有提供）
    if (!this.label) {
      this.label = this.i18nService.translate('form.approval_flow.title');
    }
    if (!this.placeholder) {
      this.placeholder = this.i18nService.translate('form.approval_flow.placeholder');
    }

    this.loadDepartments();
    this.loadApprovalFlows();
    console.log("initiatorInfo", this.initiatorInfo);
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 當 approvalFlowId 改變時，更新選中的流程
    if (changes['approvalFlowId'] && this.approvalFlowId && this.availableFlows.length > 0) {
      this.updateSelectedFlowById(this.approvalFlowId);
    }
  }


  loadApprovalFlows(): void {
    this.loading = true;
    this.error = null;

    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      this.error = this.i18nService.translate('form.approval_flow.missing_company_id_or_assistant');
      this.loading = false;
      return;
    }

    this.approvalService.getApprovalFlowDefinitions(companyid, assistantNo).subscribe({
      next: (response) => {

        console.log('Approval flows loaded:', response);

        if (response && response.success && response.data) {
          this.availableFlows = response.data.filter((flow: ApprovalFlowDefinition) => flow.status === 'active');
          console.log('Available flows after filtering:', this.availableFlows);

          // 手動觸發變更檢測
          this.cdr.detectChanges();
        } else {
          this.availableFlows = [];
          console.log('No data received or response format incorrect');
        }
        this.loading = false;

        // 優先使用 approvalFlowId（來自父元件的設定）
        if (this.approvalFlowId) {
          this.updateSelectedFlowById(this.approvalFlowId);
          // 如果指定了 approvalFlowId，直接查詢該流程的詳細資訊
          console.log('Loading specific approval flow:', this.approvalFlowId);
          this.loadSpecificApprovalFlow(this.approvalFlowId);
        } else if (this.value) {
          // 如果沒有 approvalFlowId，則使用表單值
          this.updateSelectedFlow(this.value);
        }
      },
      error: (err: any) => {
        this.error = this.i18nService.translate('form.approval_flow.load_error');
        console.error('Error loading approval flows', err);
        this.loading = false;
      }
    });
  }

  private updateSelectedFlowById(flowId: string): void {
    this.selectedFlow = this.availableFlows.find(flow => flow.id === flowId) || null;
    // 在預覽模式下，不需要更新表單值
    if (!this.preview) {
      this.value = flowId;
    }
  }

  onFlowChange(flowId: string): void {
    this.value = flowId;
    this.updateSelectedFlow(flowId);

    // 輸出完整的簽核流程資訊，而不只是 flowId
    const fullApprovalData = this.generateFullApprovalData();
    this.onChange(fullApprovalData);
  }

  private updateSelectedFlow(flowId: string): void {
    this.selectedFlow = this.availableFlows.find(flow => flow.id === flowId) || null;
  }

  // ControlValueAccessor 介面實作
  writeValue(value: any): void {
    this.value = value || '';
    if (this.value && this.availableFlows.length > 0) {
      this.updateSelectedFlow(this.value);
    }
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState?(isDisabled: boolean): void {
    // 可以在這裡實作禁用狀態的邏輯
  }

  // 新增方法：判斷值是否為解析後的簽核流程資料
  isResolvedFlowData(value: any): value is ResolvedApprovalStep[] {
    return Array.isArray(value) && value.length > 0 &&
           value[0] && typeof value[0] === 'object' &&
           'level' in value[0] && 'stepName' in value[0] && 'approverName' in value[0];
  }

  // 新增方法：載入特定的簽核流程資訊
  private loadSpecificApprovalFlow(flowId: string): void {
    console.log('載入特定簽核流程:', flowId);

    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      console.error(this.i18nService.translate('form.approval_flow.missing_required_params'));
      return;
    }

    this.approvalService.getApprovalFlowDefinitionById(flowId, companyid, assistantNo).subscribe({
      next: (flowDef) => {
        if (flowDef) {
          console.log('載入的簽核流程定義:', flowDef);
          this.selectedFlow = flowDef;
          // 載入完成後，預載入所有指定使用者的名稱
          // this.preloadUserNames();

          // 只有在 getuserlist 為 true 時才載入人員列表
          if (this.getuserlist) {
            this.loadApprovalUserLists();
          } else {
            // 即使不載入人員列表，也要輸出基本的簽核流程資料
            this.updateFullApprovalData();
          }
          this.cdr.detectChanges();
        } else {
          console.warn(this.i18nService.translate('form.approval_flow.flow_not_found', { flowId }));
        }
      },
      error: (error) => {
        console.error(this.i18nService.translate('form.approval_flow.load_flow_definition_error'), error);
      }
    });
  }

  // 新增方法：載入部門資料
  loadDepartments(): void {
    this.departmentService.getAllDepartments().subscribe({
      next: (departments) => {
        this.departments = departments;
        console.log('Departments loaded successfully:', departments.length, 'departments');
      },
      error: (err) => {
        console.error('Error loading departments', err);
      }
    });
  }

  // 取得角色選項
  get roleOptions(): RoleOption[] {
    return this.authService.roleOptions;
  }

  // 取得部門名稱
  getDepartmentName(departmentId: string, step?: any): string {
    if (!departmentId) return '';

    // 處理動態部門
    if (departmentId === 'submitter_dept') {
      // 只有在 getuserlist 為 true 時才顯示實際部門名稱
      if (this.getuserlist) {
        const actualName = step?.actualDepartmentName || '';
        return actualName ? this.i18nService.translate('form.approval_flow.same_as_submitter_dept_with_name', { actualName }) : this.i18nService.translate('form.approval_flow.same_as_submitter_dept');
      } else {
        return this.i18nService.translate('form.approval_flow.same_as_submitter_dept');
      }
    }
    if (departmentId === 'parent_dept') {
      // 只有在 getuserlist 為 true 時才顯示實際部門名稱
      if (this.getuserlist) {
        const actualName = step?.actualDepartmentName || '';
        return actualName ? this.i18nService.translate('form.approval_flow.submitter_parent_dept_with_name', { actualName }) : this.i18nService.translate('form.approval_flow.submitter_parent_dept');
      } else {
        return this.i18nService.translate('form.approval_flow.submitter_parent_dept');
      }
    }

    // 處理固定部門
    const dept = this.departments.find(d => d.id === departmentId);
    return dept?.name || this.i18nService.translate('form.approval_flow.unknown_department', { departmentId });
  }

  // 取得角色的翻譯名稱（支援多國語系）
  getRoleTranslatedName(roleId: string): string {
    if (!roleId) return '';

    // 先檢查是否為基礎角色，如果是則使用翻譯
    const roleOption = this.roleOptions.find(option => option.value === roleId);
    if (roleOption) {
      return roleOption.label;
    }

    // 如果不是基礎角色，則使用原本的名稱
    return this.i18nService.translate('form.approval_flow.unknown_role', { roleId });
  }

  // 新增方法：切換步驟展開/收合
  toggleStepsExpanded(): void {
    this.isStepsExpanded = !this.isStepsExpanded;
  }

  // 新增方法：取得使用者名稱
  getUserName(userId: string): string {
    if (!userId) return this.i18nService.translate('form.approval_flow.unspecified');

    // 檢查是否已經有 userName
    if (this.selectedFlow && this.selectedFlow.steps) {
      const step = this.selectedFlow.steps.find(s => s.userId === userId);
      if (step && step.userName) {
        return step.userName;
      }
    }

    // 如果沒有 userName，且還沒有在載入中，才發送請求
    if (!this.isLoadingUserName(userId)) {
      this.loadUserName(userId);
    }

    return userId; // 預設回傳 userId
  }

  // 追蹤正在載入的使用者名稱
  private loadingUserNames = new Set<string>();

  // 檢查是否正在載入特定使用者名稱
  private isLoadingUserName(userId: string): boolean {
    return this.loadingUserNames.has(userId);
  }

  // 載入使用者名稱（只載入一次）
  private loadUserName(userId: string): void {
    if (this.loadingUserNames.has(userId)) {
      return; // 已在載入中，不重複載入
    }

    this.loadingUserNames.add(userId);

    this.userService.getUsernameById(userId).subscribe({
      next: (username) => {
        this.loadingUserNames.delete(userId);
        if (username) {
          // 找到對應的步驟並更新其 userName 屬性
          if (this.selectedFlow && this.selectedFlow.steps) {
            const step = this.selectedFlow.steps.find(s => s.userId === userId);
            if (step) {
              step.userName = username;
              this.cdr.detectChanges(); // 觸發變更檢測
            }
          }
        }
      },
      error: (error) => {
        this.loadingUserNames.delete(userId);
        console.error(this.i18nService.translate('form.approval_flow.get_username_error'), error);
      }
    });
  }

  // 輔助方法：檢查步驟是否有使用者列表
  hasUserList(step: any): boolean {
    return step && step.userlist && Array.isArray(step.userlist) && step.userlist.length > 0;
  }

  // 輔助方法：取得步驟的使用者列表
  getUserList(step: any): any[] {
    return (step && step.userlist && Array.isArray(step.userlist)) ? step.userlist : [];
  }

  // 預載入所有指定人員的使用者名稱
  preloadUserNames(): void {
    if (!this.selectedFlow || !this.selectedFlow.steps) {
      return;
    }

    // 如果 getuserlist 為 true，則載入簽核人員列表
    if (this.getuserlist) {
      console.log(this.i18nService.translate('form.approval_flow.getting_user_list'));
      this.loadApprovalUserLists();
    }
  }

  // 新增方法：載入簽核流程對應的人員列表
  private loadApprovalUserLists(): void {

    if (!this.selectedFlow || !this.selectedFlow.steps) {
      return;
    }

    // 遍歷每個步驟，為每個步驟建立 userlist 陣列
    this.selectedFlow.steps.forEach((step, index) => {
      // 初始化 userlist 陣列
      if (!step.hasOwnProperty('userlist')) {
        (step as any).userlist = [];
      }

      if (step.approverType === 'specific_user' && step.userId) {

        console.log(`處理指定人員 (userId: ${step.userId})`);
        // 指定人員：直接將該人員的 username 和 id 寫入
        this.userService.getUsernameById(step.userId).subscribe({
          next: (username) => {
            if (username) {
              (step as any).userlist = [{
                id: step.userId,
                name: username
              }];
              // 更新完整資料並通知父元件
              this.updateFullApprovalData();
            }
          },
          error: (error) => {
            console.error(this.i18nService.translate('form.approval_flow.get_specific_user_error', { userId: step.userId || '' }), error);
          }
        });
      } else if (step.approverType === 'role_based' && step.departmentId && step.roleId) {

        console.log(`處理部門角色 (dept: ${step.departmentId}, role: ${step.roleId})`);

        // 處理動態部門 ID
        let actualDepartmentId = step.departmentId;

        if (step.departmentId === 'submitter_dept') {
          // 取得填寫者的部門 ID
          actualDepartmentId = this.initiatorInfo?.departmentId || '';

          // 同時取得實際部門名稱
          if (actualDepartmentId) {
            const dept = this.departments.find(d => d.id === actualDepartmentId);
            (step as any).actualDepartmentName = dept?.name || '';
          }
        } else if (step.departmentId === 'parent_dept') {
          // 取得填寫者的上級部門 ID（異步操作）
          this.departmentService.getParentDepartmentId(this.initiatorInfo?.departmentId || '').subscribe({
            next: (parentDeptId) => {
              if (parentDeptId && step.roleId) {
                // 取得實際部門名稱
                const dept = this.departments.find(d => d.id === parentDeptId);
                (step as any).actualDepartmentName = dept?.name || '';

                this.getDepartmentRoleUsers(parentDeptId, step.roleId).subscribe({
                  next: (users: any[]) => {
                    (step as any).userlist = users.map((user: any) => ({
                      id: user.id,
                      name: user.username || user.name
                    }));
                    // 更新完整資料並通知父元件
                    this.updateFullApprovalData();
                  },
                  error: (error: any) => {
                    console.error(this.i18nService.translate('form.approval_flow.get_parent_dept_role_users_error', { parentDeptId, roleId: step.roleId || '' }), error);
                  }
                });
              } else {
                console.warn(this.i18nService.translate('form.approval_flow.incomplete_parent_dept_info', { parentDeptId: parentDeptId || '', roleId: step.roleId || '' }));
              }
            },
            error: (error) => {
              console.error(this.i18nService.translate('form.approval_flow.get_parent_dept_id_error'), error);
            }
          });
          return; // 因為是異步操作，直接返回，不執行後面的同步邏輯
        }

        // 部門+角色：透過 API 取得該部門該角色的所有人員
        if (actualDepartmentId && step.roleId) {
          this.getDepartmentRoleUsers(actualDepartmentId, step.roleId).subscribe({
            next: (users: any[]) => {
              (step as any).userlist = users.map((user: any) => ({
                id: user.id,
                name: user.username || user.name
              }));
              // 更新完整資料並通知父元件
              this.updateFullApprovalData();
            },
            error: (error: any) => {
              console.error(this.i18nService.translate('form.approval_flow.get_dept_role_users_error', { departmentId: actualDepartmentId, roleId: step.roleId || '' }), error);
            }
          });
        } else {
          console.warn(this.i18nService.translate('form.approval_flow.incomplete_dept_role_info', { departmentId: actualDepartmentId, roleId: step.roleId || '' }));
        }
      }
    });
  }

  // 新增方法：取得特定部門特定角色的使用者列表
  private getDepartmentRoleUsers(departmentId: string, roleId: string): Observable<any[]> {
    const companyid = environment.companyid;
    const assistantNo = this.router.routerState.snapshot.root.queryParams['id'];

    if (!companyid || !assistantNo) {
      console.warn(this.i18nService.translate('form.approval_flow.missing_company_id_or_assistant'));
      return of([]);
    }

    const url = `${environment.apiUrl}/api/users/department-role-users`;
    const params = {
      companyid: companyid,
      assistantNo: "ALL",   //到admin階層的組織圖找資料
      departmentid: departmentId,
      roleid: roleId
    };

    console.log('Fetching department role users:', params);

    return this.http.get<{
      success: boolean;
      data?: any[];
      error?: string;
      message?: string;
    }>(url, { params }).pipe(
      map((response: any) => {
        console.log('API Response for department role users:', response);
        if (response.success && response.data) {
          return response.data;
        } else {
          console.warn(this.i18nService.translate('form.approval_flow.api_response_error'), response);
          return [];
        }
      }),
      catchError((error: any) => {
        console.error(this.i18nService.translate('form.approval_flow.get_dept_role_users_failed'), error);
        return of([]);
      })
    );
  }

  // 新增方法：產生完整的簽核流程資料
  private generateFullApprovalData(): any {
    if (!this.selectedFlow || !this.selectedFlow.steps) {
      return '';
    }

    const approvalSteps = this.selectedFlow.steps.map((step, index) => {
      const stepData: any = {
        level: index + 1,
        stepName: step.name,
        approverType: step.approverType,
        requireComment: step.requireComment || false,
        requireSignature: step.requireSignature || false,
        optional: step.optional || false
      };

      if (step.approverType === 'role_based') {
        // 處理角色基礎的簽核
        stepData.departmentId = step.departmentId;
        stepData.roleId = step.roleId;
        stepData.departmentName = this.getDepartmentName(step.departmentId || '', step);
        stepData.roleName = this.getRoleTranslatedName(step.roleId || '');
        stepData.isDynamicDepartment = step.departmentId === 'submitter_dept' || step.departmentId === 'parent_dept';

        // 如果有實際部門名稱，也加入
        if ((step as any).actualDepartmentName) {
          stepData.actualDepartmentName = (step as any).actualDepartmentName;
        }

        // 加入可簽核人員列表
        if (this.getuserlist && (step as any).userlist) {
          stepData.approverList = (step as any).userlist;
        }
      } else if (step.approverType === 'specific_user') {
        // 處理指定人員的簽核
        stepData.userId = step.userId;
        stepData.userName = this.getUserName(step.userId || '');
        stepData.isDynamicDepartment = false;

        // 加入指定人員資訊
        if (this.getuserlist && (step as any).userlist) {
          stepData.approverList = (step as any).userlist;
        }
      }

      // 新增：處理自訂表單欄位
      if (step.customFormItems && step.customFormItems.length > 0) {
        stepData.customFormItems = step.customFormItems;
      }

      return stepData;
    });

    // 返回完整的簽核流程資料結構
    return {
      type: 'approval-flow',
      value: {
        flowId: this.selectedFlow.id,
        flowName: this.selectedFlow.name,
        flowDescription: this.selectedFlow.description,
        steps: approvalSteps
      }
    };
  }

  // 新增方法：更新完整簽核資料並通知父元件
  private updateFullApprovalData(): void {
    const fullApprovalData = this.generateFullApprovalData();
    this.onChange(fullApprovalData);
    this.cdr.detectChanges();
  }

  // 新增方法：取得表單元件類型的顯示名稱
  getFormItemTypeDisplay(type: string): string {
    const typeDisplayMap: { [key: string]: string } = {
      'text-input': this.i18nService.translate('form.builder.text_input'),
      'textarea': this.i18nService.translate('form.builder.textarea'),
      'number-input': this.i18nService.translate('form.builder.number_input'),
      'date': this.i18nService.translate('form.builder.date'),
      'datetime': this.i18nService.translate('form.builder.datetime'),
      'select': this.i18nService.translate('form.builder.select'),
      'radiobutton': this.i18nService.translate('form.builder.radiobutton'),
      'multi-checkbox': this.i18nService.translate('form.builder.multi_checkbox'),
      'file-upload': this.i18nService.translate('form.builder.file_upload'),
      'signature': this.i18nService.translate('form.builder.signature')
    };

    return typeDisplayMap[type] || type;
  }
}
